import React from 'react';
import {Box, Typography, Tooltip} from '@mui/material';
import {
    BAND_COLOR, RISK_BAND_COLORS, READINESS_BAND_COLORS, CONTEXT_BAND_COLORS,
    DEPLOYMENT_ACTION_BAND_COLORS, PARTICIPATION_BAND_COLORS,
} from '../../utils/constants';
import {SuppressedValue} from './SuppressedValue.comp';
import {UNIT, toNumber, fmtValue, benchmarkText} from './units';

/* The dashboards' shared building blocks — one copy for School, Network and
   Partner (their `components/widgets.comp.jsx` files re-export this one and only
   keep their own legend defaults). National, Class and Individual use the chip
   and the number rules from here and keep their own bars. */

export const NAVY   = '#052F44';
export const ORANGE = '#FF6900';
export const TEAL   = '#1B6B55';
// Secondary benchmark-marker colour (e.g. Global avg / School avg) — the
// existing --brand-global-2 accent, kept distinct from NAVY (primary marker)
// and from every band/tier fill colour used on the bars themselves.
export const MARKER_SECONDARY = 'var(--brand-global-2, #7B8FC7)';
// Primary benchmark-marker colour (e.g. National avg / Grade avg). Unlike
// NAVY/--brand-secondary (which never changes for dark mode), --brand-text
// actually flips to a light colour in dark mode — the markers are plain
// <Box> border-colors, not Typography, so they're invisible to the global
// `.MuiTypography-root { color: ... !important }` dark-mode override and need
// their own adaptive reference or they go pitch-black-on-near-black.
export const MARKER_PRIMARY = 'var(--brand-text, #052F44)';
export { BAND_COLOR, RISK_BAND_COLORS, READINESS_BAND_COLORS, CONTEXT_BAND_COLORS };

export const bandColor = (label) => BAND_COLOR[label] || '#6B7280';
export const readinessBandColor = (label) => {
    if (!label) return '#6B7280';
    const lowerLabel = label.toLowerCase();
    const matchedKey = Object.keys(READINESS_BAND_COLORS).find(
        (key) => key.toLowerCase() === lowerLabel
    );
    return matchedKey ? READINESS_BAND_COLORS[matchedKey] : '#6B7280';
};
export const contextBandColor = (label) => {
    if (!label) return '#6B7280';
    const lowerLabel = label.toLowerCase();
    const matchedKey = Object.keys(CONTEXT_BAND_COLORS).find(
        (key) => key.toLowerCase() === lowerLabel
    );
    return matchedKey ? CONTEXT_BAND_COLORS[matchedKey] : '#6B7280';
};
export const riskBandColor = (label) => RISK_BAND_COLORS[label] || '#6B7280';

/* Heat-map fill: the band colour at the strength every dashboard's heat-map uses
   (0.8; the context rubric's coral Low a little stronger so it does not wash out). */
export const heatFill = (hex, alpha) => {
    if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) return undefined;
    const a = alpha ?? (hex.toUpperCase() === '#E04535' ? 0.92 : 0.8);
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
};

/* One pill width per rubric family, so a legend and the rows under it line up
   (QA 9 Oct: "the chips should have a consistent width"). A label outside the
   known families keeps a minimum width and grows with its text. */
const RISK_LABELS = new Set(Object.keys(RISK_BAND_COLORS));
const DEPLOYMENT_LABELS = new Set([...Object.keys(DEPLOYMENT_ACTION_BAND_COLORS), 'Growth Opportunity']);
const SCORE_LABELS = new Set([
    ...Object.keys(READINESS_BAND_COLORS), ...Object.keys(CONTEXT_BAND_COLORS), ...Object.keys(PARTICIPATION_BAND_COLORS),
    'Early', 'Weak', 'Critical',
]);
export const chipWidth = (label) => {
    if (RISK_LABELS.has(label)) return 128;
    if (DEPLOYMENT_LABELS.has(label)) return 124;
    if (SCORE_LABELS.has(label)) return 92;
    return null;
};

/* Solid pill chip */
export const BandChip = ({label, color, width, sx}) => {
    const w = width || chipWidth(label);
    return (
        <Box sx={{
            display: 'inline-block', boxSizing: 'border-box', backgroundColor: color || BAND_COLOR[label] || '#6B7280', color: '#fff',
            fontSize: '12px', fontWeight: 700, px: 1, py: 0.4, borderRadius: '20px',
            // flexShrink 0: a legend row must never squeeze a pill below its family width.
            whiteSpace: 'nowrap', textAlign: 'center', flexShrink: 0, ...(w ? {width: `${w}px`} : {minWidth: '92px'}), ...sx,
        }}>
            {label}
        </Box>
    );
};

/* Page header — eyebrow · title · subtitle · meta line */
export const PageHeader = ({eyebrow, title, subtitle, meta}) => (
    <Box sx={{mb: 3}}>
        {eyebrow && (
            <Typography sx={{fontSize: '11px', fontWeight: 800, letterSpacing: '0.8px', textTransform: 'uppercase', color: ORANGE, mb: 0.5}}>
                {eyebrow}
            </Typography>
        )}
        <Typography sx={{fontSize: {xs: '22px', md: '23px'}, fontWeight: 800, color: NAVY, lineHeight: 1.2}}>
            {title}
        </Typography>
        {subtitle && (
            <Typography sx={{fontSize: '14px', fontStyle: 'italic', color: '#4B5563', mt: 0.75}}>{subtitle}</Typography>
        )}
        {meta && (
            <Typography sx={{fontSize: '12px', color: '#9CA3AF', mt: 0.75}}>{meta}</Typography>
        )}
    </Box>
);

/* Decision-guidance / recommended-action banner (green) */
// `text` may be a single string (one flowing sentence) or an array of
// distinct points — arrays render as a bulleted list instead of running
// together in one paragraph.
export const GuidanceBanner = ({label = 'Decision guidance:', text}) => (
    <Box sx={{
        mt: 3, p: {xs: 2, md: 2.5}, backgroundColor: 'var(--brand-surface-subtle, #F0F7F4)', border: '1px solid #C6E0D8',
        borderLeft: `4px solid ${TEAL}`, borderRadius: '0 8px 8px 0',
    }}>
        <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.7, mb: Array.isArray(text) ? 0.75 : 0}}>
            <strong style={{color: TEAL}}>{label}</strong>{!Array.isArray(text) && <>&nbsp;{text}</>}
        </Typography>
        {Array.isArray(text) && (
            <Box component="ul" sx={{m: 0, pl: 2.2}}>
                {text.map((line, i) => (
                    <Box component="li" key={i} sx={{mb: i < text.length - 1 ? 0.5 : 0}}>
                        <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.7}}>{line}</Typography>
                    </Box>
                ))}
            </Box>
        )}
    </Box>
);

/* "Use with care" warning banner (red). hideLabel drops the bold "Use with
   care." prefix while keeping the same red warning styling. */
export const UseWithCareBanner = ({text, hideLabel}) => (
    <Box sx={{
        mb: 3, p: {xs: 2, md: 2.5}, backgroundColor: 'var(--brand-surface-subtle, #FEE2E2)', border: '1px solid #FECACA',
        borderLeft: '4px solid #DC2626', borderRadius: '0 8px 8px 0',
    }}>
        <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.7}}>
            {!hideLabel && <strong style={{color: '#DC2626'}}>Use with care.&nbsp;</strong>}{text}
        </Typography>
    </Box>
);

/* Neutral informational note banner (navy left-border) */
export const NoteBanner = ({label, text, mt = 4}) => (
    <Box sx={{
        mt, p: {xs: 2, md: 2.5}, borderRadius: '10px',
        backgroundColor: 'var(--brand-surface-muted, #F3F4F6)', border: '1px solid var(--brand-border, #E5E7EB)',
        borderLeft: `4px solid ${NAVY}`,
    }}>
        {label && <Typography sx={{fontSize: '13px', fontWeight: 700, color: NAVY, mb: 0.5}}>{label}</Typography>}
        {Array.isArray(text) ? (
            text.map((line, i) => (
                <Typography key={i} sx={{fontSize: '13px', color: '#374151', lineHeight: 1.6}}>{line}</Typography>
            ))
        ) : (
            <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.6}}>{text}</Typography>
        )}
    </Box>
);

/* KPI stat card — label · value · sub · band pill */
export const StatCard = ({label, value, sub, band, bandColorOverride, topColor}) => {
    const resolvedTopColor = topColor || (band ? (bandColorOverride || bandColor(band)) : null);
    return (
        <Box sx={{
            border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #fff)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)', overflow: 'hidden', height: '100%',
            display: 'flex', flexDirection: 'column',
        }}>
            {resolvedTopColor && <Box sx={{backgroundColor: resolvedTopColor, height: '6px', flexShrink: 0}}/>}
            <Box sx={{p: 2.5, display: 'flex', flexDirection: 'column', flex: 1}}>
                <Typography sx={{fontSize: '11px', fontWeight: 700, color: '#6A7282', letterSpacing: '1px', textTransform: 'uppercase', mb: 1}}>
                    {label}
                </Typography>
                <Typography sx={{fontSize: {xs: '32px', md: '40px'}, fontWeight: 800, color: NAVY, lineHeight: 1}}>
                    {value}
                </Typography>
                {sub && <Typography sx={{fontSize: '12px', color: '#6A7282', mt: 0.75}}>{sub}</Typography>}
                {band && (
                    <Box sx={{mt: 'auto', pt: 1.5}}>
                        <BandChip label={band} color={bandColorOverride}/>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

// National-type markers render as a solid downward-pointing triangle sitting
// ABOVE the bar (tip touching the bar's top edge). Global-type markers render
// as a hollow-colored upward-pointing triangle sitting BELOW it (tip touching
// the bottom edge). Being permanently separated above/below — not just when
// close enough to collide — means two markers at the same value never stack
// into a single unhoverable target, and the arrow direction itself doubles as
// a legend (down = compared against the bar from above, up = from below).
export const primaryMarkerSx = (value) => ({
    position: 'absolute', left: `${value}%`, top: '-9px',
    width: 0, height: 0,
    borderLeft: '6px solid transparent', borderRight: '6px solid transparent',
    borderTop: `9px solid ${MARKER_PRIMARY}`,
    transform: 'translateX(-50%)', cursor: 'pointer', zIndex: 4,
});
export const secondaryMarkerSx = (value) => ({
    position: 'absolute', left: `${value}%`, bottom: '-9px',
    width: 0, height: 0,
    borderLeft: '6px solid transparent', borderRight: '6px solid transparent',
    borderBottom: `9px solid ${MARKER_SECONDARY}`,
    transform: 'translateX(-50%)', cursor: 'pointer', zIndex: 4,
});

/* Horizontal score bar (0–100) with value + band label + optional National/Global markers.
   `unit`: 'score' (default — readiness / context, no "%") or 'percent' (cyber risk,
   participation). It formats the figure, the bar tooltip and both benchmark tooltips. */
export const ScoreBar = ({label, sublabel, score, band, national, global, max = 100, bandColorOverride, suppressed, unit = UNIT.SCORE}) => {
    const rawVal = toNumber(score);
    // No value (not sent) or under the n floor (suppressed): the label only, with the
    // suppression copy — never a 0-width bar, a band chip or a benchmark marker.
    const masked = suppressed === true || rawVal === null;
    const numericScore = masked ? 0 : rawVal;
    const valueText = fmtValue(score, unit);
    const resolvedColor = bandColorOverride || bandColor(band);

    if (masked) {
        return (
            <Box sx={{mb: 2}}>
                <Box sx={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.5, gap: 1}}>
                    <Box sx={{flex: 1}}>
                        <Typography sx={{fontSize: '13.5px', fontWeight: 700, color: NAVY, lineHeight: 1.3}}>{label}</Typography>
                        {sublabel && <Typography sx={{fontSize: '11px', color: '#9CA3AF'}}>{sublabel}</Typography>}
                    </Box>
                    <Box sx={{flexShrink: 0, textAlign: 'right'}}>
                        {suppressed ? <SuppressedValue/> : <Typography sx={{fontSize: '14px', fontWeight: 800, color: NAVY, lineHeight: 1}}>—</Typography>}
                    </Box>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{mb: 2}}>
            {/* A primary (▽) marker sits above the track, so the label row leaves it room. */}
            <Box sx={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: national != null ? 1.5 : 0.5, gap: 1}}>
                <Box sx={{flex: 1}}>
                    <Typography sx={{fontSize: '13.5px', fontWeight: 700, color: NAVY, lineHeight: 1.3}}>{label}</Typography>
                    {sublabel && <Typography sx={{fontSize: '11px', color: '#9CA3AF'}}>{sublabel}</Typography>}
                </Box>
                <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, flexShrink: 0}}>
                    <Typography sx={{fontSize: '14px', fontWeight: 800, color: NAVY, lineHeight: 1}}>{valueText}</Typography>
                    {band && <BandChip label={band} color={bandColorOverride}/>}
                </Box>
            </Box>
            <Box sx={{position: 'relative', height: 12, borderRadius: '6px', backgroundColor: 'var(--brand-surface-muted, #F3F4F6)'}}>
                <Tooltip title={valueText} arrow placement="top">
                    <Box sx={{position: 'absolute', left: 0, top: 0, bottom: 0, width: `${Math.min(100, (numericScore / max) * 100)}%`, backgroundColor: resolvedColor, borderRadius: '6px', cursor: 'pointer'}}/>
                </Tooltip>
                {national != null && (
                    <Tooltip title={benchmarkText('National avg', national, unit)} arrow placement="top">
                        <Box sx={primaryMarkerSx(national)}/>
                    </Tooltip>
                )}
                {global != null && (
                    <Tooltip title={benchmarkText('Global avg', global, unit)} arrow placement="bottom">
                        <Box sx={secondaryMarkerSx(global)}/>
                    </Tooltip>
                )}
            </Box>
        </Box>
    );
};

/* Legend for the dashboard's own score (a swatch, or the rubric's band chips when
   bandColors is passed) + National avg (▽ above) + Global avg (△ below). A
   dashboard without a national comparison (the School Network) passes
   showNational={false}. */
export const BenchmarkLegend = ({
    seriesLabel = 'Score', seriesColor = '#EFA020', bandColors,
    showNational = true, showGlobal = true, nationalLabel = 'National avg', globalLabel = 'Global avg',
}) => (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap'}}>
        {bandColors ? (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap'}}>
                {Object.keys(bandColors).map((band) => (
                    <BandChip key={band} label={band} color={bandColors[band]}/>
                ))}
            </Box>
        ) : (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <Box sx={{width: 22, height: 12, borderRadius: '3px', backgroundColor: seriesColor}}/>
                <Typography sx={{fontSize: '12px', color: '#6A7282'}}>{seriesLabel}</Typography>
            </Box>
        )}
        {showNational && (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <Box sx={{width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: `7px solid ${MARKER_PRIMARY}`}}/>
                <Typography sx={{fontSize: '12px', color: '#6A7282'}}>{nationalLabel}</Typography>
            </Box>
        )}
        {showGlobal && (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <Box sx={{width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderBottom: `7px solid ${MARKER_SECONDARY}`}}/>
                <Typography sx={{fontSize: '12px', color: '#6A7282'}}>{globalLabel}</Typography>
            </Box>
        )}
    </Box>
);

/* Band chips + a single ▽ marker label — the By Class pages (SD slide 71),
   whose only marker is the grade average. */
export const BandMarkerLegend = ({bandColors, markerLabel}) => (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 3}}>
        {Object.keys(bandColors).map((band) => (
            <BandChip key={band} label={band} color={bandColors[band]}/>
        ))}
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, ml: 1}}>
            <Box sx={{width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: `7px solid ${MARKER_PRIMARY}`}}/>
            <Typography sx={{fontSize: '12px', color: '#6A7282'}}>{markerLabel}</Typography>
        </Box>
    </Box>
);

/* Bar track (0–100) with fill + optional National (▽ above) & Global (△ below) markers.
   `unit` as on ScoreBar: 'score' (default) or 'percent'. `nationalLabel` names the ▽ marker in its tooltip.
   `bordered` draws a darker, outlined track so the bar still shows at 0 (the Program Impact pass-rate card). */
export const MarkerBar = ({score, color, national, global, height = 14, suppressed, unit = UNIT.SCORE, nationalLabel = 'National avg', bordered = false}) => {
    // Under the n floor: the copy in place of the track (no sliver, no markers).
    if (suppressed) return <Box sx={{flex: 1}}><SuppressedValue/></Box>;
    const n = toNumber(score);
    const has = n !== null;
    const width = has ? Math.min(100, Math.max(0, n)) : 0;
    const trackSx = bordered
        ? {backgroundColor: 'var(--brand-border, #E5E7EB)', border: '1px solid #D1D5DB', boxSizing: 'border-box'}
        : {backgroundColor: 'var(--brand-surface-muted, #F3F4F6)'};
    return (
    <Box sx={{position: 'relative', flex: 1, height: `${height}px`, ...trackSx, borderRadius: `${height / 2}px`}}>
        <Tooltip title={has ? fmtValue(score, unit) : '—'} arrow placement="top">
            <Box sx={{position: 'absolute', left: 0, top: 0, bottom: 0, width: `${width}%`, backgroundColor: color, borderRadius: `${height / 2}px`, cursor: 'pointer', minWidth: has ? '4px' : 0}}/>
        </Tooltip>
        {national != null && (
            <Tooltip title={benchmarkText(nationalLabel, national, unit)} arrow placement="top">
                <Box sx={primaryMarkerSx(national)}/>
            </Tooltip>
        )}
        {global != null && (
            <Tooltip title={benchmarkText('Global avg', global, unit)} arrow placement="bottom">
                <Box sx={secondaryMarkerSx(global)}/>
            </Tooltip>
        )}
    </Box>
    );
};

/* Shared table cell styles */
export const headCell = {backgroundColor: NAVY, color: '#fff', fontSize: '13px', fontWeight: 700, borderBottom: 'none', py: 1.75, px: 2};
export const bodyCell = {fontSize: '13px', color: '#374151', py: 1.6, px: 2, borderBottom: '1px solid #F3F4F6'};
